'use client';

import React, { useState } from 'react';
import { Plus, Search, Eye, CheckCircle, XCircle, Clock, Truck, PackageCheck, AlertTriangle, X, Layers, FileText, Send,  } from 'lucide-react';
import { FARMS, HOUSES_BY_FARM, MOCK_FLOCK_SUMMARIES } from '../../components/birdRecordsData';

// ─── Types ────────────────────────────────────────────────────────────────────

type TransferStatus =
  | 'draft' |'pending_approval' |'approved' |'in_transit' |'received' |'rejected' |'cancelled';

interface BirdTransfer {
  id: string;
  transferNumber: string;
  transferDate: string;
  sourceFarm: string;
  sourceHouse: string;
  sourceFlock: string;
  destinationFarm: string;
  destinationHouse: string;
  destinationFlock: string;
  birdType: string;
  breed: string;
  quantityDispatched: number;
  quantityReceived?: number;
  variance?: number;
  varianceReason?: string;
  reason: string;
  transferredBy: string;
  receivedBy?: string;
  approvedBy?: string;
  status: TransferStatus;
  notes?: string;
  dispatchDate?: string;
  receiptDate?: string;
  createdAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_TRANSFERS: BirdTransfer[] = [
  {
    id: 'tr-001',
    transferNumber: 'TRF-2026-000001',
    transferDate: '2026-08-15',
    sourceFarm: 'Idudi Poultry Farm',
    sourceHouse: 'House A1',
    sourceFlock: 'BR-2026-001',
    destinationFarm: 'Idudi Poultry Farm',
    destinationHouse: 'House A2',
    destinationFlock: 'BR-2026-002',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantityDispatched: 200,
    quantityReceived: 198,
    variance: -2,
    varianceReason: 'Two birds died in transit — heat stress',
    reason: 'Rebalancing house density',
    transferredBy: 'James Okello',
    receivedBy: 'Sarah Namukasa',
    approvedBy: 'Farm Manager',
    status: 'received',
    dispatchDate: '2026-08-15',
    receiptDate: '2026-08-15',
    createdAt: '2026-08-15T08:00:00Z',
  },
  {
    id: 'tr-002',
    transferNumber: 'TRF-2026-000002',
    transferDate: '2026-08-22',
    sourceFarm: 'Idudi Poultry Farm',
    sourceHouse: 'Layer House 01',
    sourceFlock: 'LY-2026-001',
    destinationFarm: 'Kampala Demo Farm',
    destinationHouse: 'Layer House 03',
    destinationFlock: 'LY-2026-003',
    birdType: 'Layer',
    breed: 'Lohmann Brown',
    quantityDispatched: 500,
    quantityReceived: 500,
    variance: 0,
    reason: 'Farm expansion — Kampala branch stocking',
    transferredBy: 'Moses Wafula',
    receivedBy: 'Peter Ssemakula',
    approvedBy: 'Operations Director',
    status: 'received',
    dispatchDate: '2026-08-22',
    receiptDate: '2026-08-22',
    createdAt: '2026-08-22T07:00:00Z',
  },
  {
    id: 'tr-003',
    transferNumber: 'TRF-2026-000003',
    transferDate: '2026-09-01',
    sourceFarm: 'Idudi Poultry Farm',
    sourceHouse: 'House B1',
    sourceFlock: 'BR-2026-003',
    destinationFarm: 'Idudi Poultry Farm',
    destinationHouse: 'House A2',
    destinationFlock: 'BR-2026-002',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantityDispatched: 150,
    reason: 'Consolidation — House B1 maintenance',
    transferredBy: 'James Okello',
    approvedBy: 'Farm Manager',
    status: 'in_transit',
    dispatchDate: '2026-09-01',
    createdAt: '2026-09-01T09:00:00Z',
  },
  {
    id: 'tr-004',
    transferNumber: 'TRF-2026-000004',
    transferDate: '2026-09-04',
    sourceFarm: 'Idudi Poultry Farm',
    sourceHouse: 'Layer House 02',
    sourceFlock: 'LY-2026-002',
    destinationFarm: 'Kampala Demo Farm',
    destinationHouse: 'House D2',
    destinationFlock: 'LY-2026-004',
    birdType: 'Layer',
    breed: 'ISA Brown',
    quantityDispatched: 300,
    reason: 'Kampala branch restocking',
    transferredBy: 'Moses Wafula',
    status: 'pending_approval',
    createdAt: '2026-09-04T10:00:00Z',
  },
  {
    id: 'tr-005',
    transferNumber: 'TRF-2026-000005',
    transferDate: '2026-09-05',
    sourceFarm: 'Idudi Poultry Farm',
    sourceHouse: 'Breeder House 01',
    sourceFlock: 'BRD-2026-001',
    destinationFarm: 'Idudi Poultry Farm',
    destinationHouse: 'House A1',
    destinationFlock: 'BR-2026-001',
    birdType: 'Breeder',
    breed: 'Ross Breeder',
    quantityDispatched: 50,
    reason: 'Breeding program adjustment',
    transferredBy: 'Sarah Namukasa',
    status: 'draft',
    createdAt: '2026-09-05T11:00:00Z',
  },
];

// ─── Status Config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<TransferStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: '#d97706', bg: '#fef3c7', icon: Clock },
  approved: { label: 'Approved', color: '#2563eb', bg: '#dbeafe', icon: CheckCircle },
  in_transit: { label: 'In Transit', color: '#7c3aed', bg: '#ede9fe', icon: Truck },
  received: { label: 'Received', color: '#16a34a', bg: '#dcfce7', icon: PackageCheck },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', icon: X },
};

// ─── New Transfer Form ────────────────────────────────────────────────────────

interface NewTransferFormProps {
  onClose: () => void;
  onSubmit: (t: Partial<BirdTransfer>) => void;
}

function NewTransferForm({ onClose, onSubmit }: NewTransferFormProps) {
  const [form, setForm] = useState({
    sourceFarm: FARMS[0],
    sourceHouse: '',
    sourceFlock: '',
    destinationFarm: FARMS[0],
    destinationHouse: '',
    destinationFlock: '',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    quantityDispatched: '',
    reason: '',
    transferredBy: '',
    notes: '',
    requireApproval: true,
  });

  const sourceHouses = HOUSES_BY_FARM[form.sourceFarm] || [];
  const destHouses = HOUSES_BY_FARM[form.destinationFarm] || [];
  const sourceFlocks = MOCK_FLOCK_SUMMARIES.filter(f => f.farm === form.sourceFarm && (!form.sourceHouse || f.house === form.sourceHouse));
  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === form.sourceFlock);

  const set = (k: string, v: string | boolean) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...form,
      quantityDispatched: Number(form.quantityDispatched),
      status: form.requireApproval ? 'pending_approval' : 'approved',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">New Bird Transfer</h2>
            <p className="text-xs text-gray-500 mt-0.5">Move birds between houses, farms, or flocks</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Section A — Source */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Send size={12} /> Source Location
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source Farm *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.sourceFarm} onChange={e => set('sourceFarm', e.target.value)} required>
                  {FARMS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source House *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.sourceHouse} onChange={e => set('sourceHouse', e.target.value)} required>
                  <option value="">Select house</option>
                  {sourceHouses.map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source Flock *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.sourceFlock} onChange={e => set('sourceFlock', e.target.value)} required>
                  <option value="">Select flock</option>
                  {sourceFlocks.map(f => <option key={f.flockNumber} value={f.flockNumber}>{f.flockNumber} — {f.flockName}</option>)}
                </select>
              </div>
            </div>
            {selectedFlock && (
              <div className="mt-2 p-2.5 bg-blue-50 rounded-lg flex items-center gap-4 text-xs text-blue-700">
                <span>Available: <strong>{selectedFlock.currentBirds.toLocaleString()}</strong> birds</span>
                <span>Breed: <strong>{selectedFlock.breed}</strong></span>
                <span>Type: <strong>{selectedFlock.birdType}</strong></span>
              </div>
            )}
          </div>

          {/* Section B — Destination */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <PackageCheck size={12} /> Destination Location
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Destination Farm *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.destinationFarm} onChange={e => set('destinationFarm', e.target.value)} required>
                  {FARMS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Destination House *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.destinationHouse} onChange={e => set('destinationHouse', e.target.value)} required>
                  <option value="">Select house</option>
                  {destHouses.map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Destination Flock</label>
                <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="e.g. BR-2026-002" value={form.destinationFlock} onChange={e => set('destinationFlock', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Section C — Birds */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Layers size={12} /> Bird Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bird Type *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.birdType} onChange={e => set('birdType', e.target.value)} required>
                  {['Broiler','Layer','Breeder','Chick','Pullet','Cockerel','Local/Indigenous','Other'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Breed *</label>
                <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="e.g. Cobb 500" value={form.breed} onChange={e => set('breed', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Quantity to Dispatch *</label>
                <input type="number" min="1" className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="0" value={form.quantityDispatched} onChange={e => set('quantityDispatched', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* Section D — Details */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FileText size={12} /> Transfer Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason *</label>
                <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Reason for transfer" value={form.reason} onChange={e => set('reason', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Transferred By *</label>
                <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Staff name" value={form.transferredBy} onChange={e => set('transferredBy', e.target.value)} required />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none" placeholder="Additional notes..." value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input type="checkbox" id="req-approval" checked={form.requireApproval} onChange={e => set('requireApproval', e.target.checked)} className="rounded" />
              <label htmlFor="req-approval" className="text-xs text-gray-700">Require approval before dispatch</label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--primary)' }}>
              {form.requireApproval ? 'Submit for Approval' : 'Create Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Receipt Form ─────────────────────────────────────────────────────────────

interface ReceiptFormProps {
  transfer: BirdTransfer;
  onClose: () => void;
  onSubmit: (id: string, received: number, reason: string, receivedBy: string) => void;
}

function ReceiptForm({ transfer, onClose, onSubmit }: ReceiptFormProps) {
  const [received, setReceived] = useState(transfer.quantityDispatched.toString());
  const [reason, setReason] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const variance = Number(received) - transfer.quantityDispatched;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(transfer.id, Number(received), reason, receivedBy);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Record Receipt</h2>
            <p className="text-xs text-gray-500 mt-0.5">{transfer.transferNumber}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">From:</span><span className="font-medium">{transfer.sourceFarm} / {transfer.sourceHouse}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Flock:</span><span className="font-medium">{transfer.sourceFlock}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Dispatched:</span><span className="font-semibold text-blue-700">{transfer.quantityDispatched.toLocaleString()} birds</span></div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Quantity Received *</label>
            <input type="number" min="0" max={transfer.quantityDispatched} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={received} onChange={e => setReceived(e.target.value)} required />
          </div>
          {variance !== 0 && (
            <div className={`p-3 rounded-lg text-xs flex items-start gap-2 ${variance < 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Variance detected: {variance > 0 ? '+' : ''}{variance} birds</p>
                <p className="mt-0.5 opacity-80">A variance reason is required to proceed.</p>
              </div>
            </div>
          )}
          {variance !== 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Variance Reason *</label>
              <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Explain the variance..." value={reason} onChange={e => setReason(e.target.value)} required={variance !== 0} />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Received By *</label>
            <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Staff name" value={receivedBy} onChange={e => setReceivedBy(e.target.value)} required />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--primary)' }}>Confirm Receipt</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Transfer Detail Drawer ───────────────────────────────────────────────────

interface TransferDetailProps {
  transfer: BirdTransfer;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onDispatch: (id: string) => void;
  onReceive: (t: BirdTransfer) => void;
}

function TransferDetail({ transfer, onClose, onApprove, onReject, onDispatch, onReceive }: TransferDetailProps) {
  const cfg = STATUS_CONFIG[transfer.status];
  const StatusIcon = cfg.icon;
  const variance = transfer.variance ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{transfer.transferNumber}</h2>
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
              <StatusIcon size={11} />{cfg.label}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"><X size={16} className="text-gray-500" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Route */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs font-semibold text-blue-700 mb-1.5 flex items-center gap-1"><Send size={11} /> Source</p>
              <p className="text-xs font-medium text-gray-900">{transfer.sourceFarm}</p>
              <p className="text-xs text-gray-600">{transfer.sourceHouse}</p>
              <p className="text-xs text-gray-500">{transfer.sourceFlock}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1"><PackageCheck size={11} /> Destination</p>
              <p className="text-xs font-medium text-gray-900">{transfer.destinationFarm}</p>
              <p className="text-xs text-gray-600">{transfer.destinationHouse}</p>
              <p className="text-xs text-gray-500">{transfer.destinationFlock}</p>
            </div>
          </div>

          {/* Bird Info */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2.5 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-500">Dispatched</p>
              <p className="text-lg font-semibold text-gray-900">{transfer.quantityDispatched.toLocaleString()}</p>
            </div>
            <div className="p-2.5 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-500">Received</p>
              <p className="text-lg font-semibold text-gray-900">{transfer.quantityReceived?.toLocaleString() ?? '—'}</p>
            </div>
            <div className={`p-2.5 rounded-lg text-center ${variance < 0 ? 'bg-red-50' : variance > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
              <p className="text-xs text-gray-500">Variance</p>
              <p className={`text-lg font-semibold ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                {variance === 0 ? '—' : (variance > 0 ? '+' : '') + variance}
              </p>
            </div>
          </div>

          {transfer.varianceReason && (
            <div className="p-3 bg-amber-50 rounded-lg flex items-start gap-2 text-xs text-amber-800">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <div><span className="font-semibold">Variance Reason: </span>{transfer.varianceReason}</div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 text-xs">
            {[
              { label: 'Bird Type', value: transfer.birdType },
              { label: 'Breed', value: transfer.breed },
              { label: 'Reason', value: transfer.reason },
              { label: 'Transferred By', value: transfer.transferredBy },
              { label: 'Received By', value: transfer.receivedBy || '—' },
              { label: 'Approved By', value: transfer.approvedBy || '—' },
              { label: 'Transfer Date', value: transfer.transferDate },
              { label: 'Dispatch Date', value: transfer.dispatchDate || '—' },
              { label: 'Receipt Date', value: transfer.receiptDate || '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900">{value}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {transfer.status === 'pending_approval' && (
              <>
                <button onClick={() => { onApprove(transfer.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 transition-colors">
                  <CheckCircle size={13} /> Approve
                </button>
                <button onClick={() => { onReject(transfer.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 transition-colors">
                  <XCircle size={13} /> Reject
                </button>
              </>
            )}
            {transfer.status === 'approved' && (
              <button onClick={() => { onDispatch(transfer.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-purple-600 hover:bg-purple-700 transition-colors">
                <Truck size={13} /> Mark In Transit
              </button>
            )}
            {transfer.status === 'in_transit' && (
              <button onClick={() => onReceive(transfer)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors">
                <PackageCheck size={13} /> Record Receipt
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdTransfersContent() {
  const [transfers, setTransfers] = useState<BirdTransfer[]>(MOCK_TRANSFERS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TransferStatus | 'all'>('all');
  const [farmFilter, setFarmFilter] = useState('all');
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<BirdTransfer | null>(null);
  const [receiptTransfer, setReceiptTransfer] = useState<BirdTransfer | null>(null);

  const nextNumber = `TRF-2026-${String(transfers.length + 1).padStart(6, '0')}`;

  const filtered = transfers.filter(t => {
    const matchSearch = !search || [t.transferNumber, t.sourceFarm, t.sourceHouse, t.sourceFlock, t.destinationFarm, t.destinationHouse, t.birdType, t.breed].some(v => v.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchFarm = farmFilter === 'all' || t.sourceFarm === farmFilter || t.destinationFarm === farmFilter;
    return matchSearch && matchStatus && matchFarm;
  });

  const stats = {
    total: transfers.length,
    pending: transfers.filter(t => t.status === 'pending_approval').length,
    inTransit: transfers.filter(t => t.status === 'in_transit').length,
    received: transfers.filter(t => t.status === 'received').length,
    withVariance: transfers.filter(t => t.variance && t.variance !== 0).length,
    totalDispatched: transfers.filter(t => ['in_transit','received'].includes(t.status)).reduce((s, t) => s + t.quantityDispatched, 0),
  };

  const handleNewTransfer = (data: Partial<BirdTransfer>) => {
    const newT: BirdTransfer = {
      id: `tr-${Date.now()}`,
      transferNumber: nextNumber,
      transferDate: new Date().toISOString().split('T')[0],
      sourceFarm: data.sourceFarm || '',
      sourceHouse: data.sourceHouse || '',
      sourceFlock: data.sourceFlock || '',
      destinationFarm: data.destinationFarm || '',
      destinationHouse: data.destinationHouse || '',
      destinationFlock: data.destinationFlock || '',
      birdType: data.birdType || 'Broiler',
      breed: data.breed || '',
      quantityDispatched: data.quantityDispatched || 0,
      reason: data.reason || '',
      transferredBy: data.transferredBy || '',
      notes: data.notes,
      status: data.status as TransferStatus || 'pending_approval',
      createdAt: new Date().toISOString(),
    };
    setTransfers(p => [newT, ...p]);
  };

  const handleApprove = (id: string) => setTransfers(p => p.map(t => t.id === id ? { ...t, status: 'approved', approvedBy: 'Farm Manager' } : t));
  const handleReject = (id: string) => setTransfers(p => p.map(t => t.id === id ? { ...t, status: 'rejected' } : t));
  const handleDispatch = (id: string) => setTransfers(p => p.map(t => t.id === id ? { ...t, status: 'in_transit', dispatchDate: new Date().toISOString().split('T')[0] } : t));
  const handleReceipt = (id: string, received: number, reason: string, receivedBy: string) => {
    setTransfers(p => p.map(t => {
      if (t.id !== id) return t;
      const variance = received - t.quantityDispatched;
      return { ...t, status: 'received', quantityReceived: received, variance, varianceReason: reason || undefined, receivedBy, receiptDate: new Date().toISOString().split('T')[0] };
    }));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Bird Transfers</h1>
          <p className="text-xs text-gray-500 mt-0.5">Move birds between houses, farms, or flocks with full dispatch and receipt tracking</p>
        </div>
        <button onClick={() => setShowNewForm(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={14} /> New Transfer
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Transfers', value: stats.total, color: '#2563eb', bg: '#dbeafe' },
          { label: 'Pending Approval', value: stats.pending, color: '#d97706', bg: '#fef3c7' },
          { label: 'In Transit', value: stats.inTransit, color: '#7c3aed', bg: '#ede9fe' },
          { label: 'Received', value: stats.received, color: '#16a34a', bg: '#dcfce7' },
          { label: 'With Variance', value: stats.withVariance, color: '#dc2626', bg: '#fee2e2' },
          { label: 'Birds Dispatched', value: stats.totalDispatched.toLocaleString(), color: '#0891b2', bg: '#cffafe' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className="p-3 rounded-xl border border-gray-100 shadow-sm bg-white">
            <p className="text-xs text-gray-500 truncate">{label}</p>
            <p className="text-xl font-semibold mt-1" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Search transfers..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white" value={statusFilter} onChange={e => setStatusFilter(e.target.value as TransferStatus | 'all')}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white" value={farmFilter} onChange={e => setFarmFilter(e.target.value)}>
          <option value="all">All Farms</option>
          {FARMS.map(f => <option key={f}>{f}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Transfer #', 'Date', 'Source', 'Destination', 'Bird Type', 'Dispatched', 'Received', 'Variance', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-semibold text-gray-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No transfers found</td></tr>
              ) : filtered.map(t => {
                const cfg = STATUS_CONFIG[t.status];
                const StatusIcon = cfg.icon;
                const variance = t.variance ?? null;
                return (
                  <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2.5 font-mono font-medium text-blue-700">{t.transferNumber}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{t.transferDate}</td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900 truncate max-w-[120px]">{t.sourceFarm}</p>
                      <p className="text-gray-500">{t.sourceHouse} / {t.sourceFlock}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900 truncate max-w-[120px]">{t.destinationFarm}</p>
                      <p className="text-gray-500">{t.destinationHouse}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900">{t.birdType}</p>
                      <p className="text-gray-500">{t.breed}</p>
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-gray-900">{t.quantityDispatched.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-gray-700">{t.quantityReceived?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2.5">
                      {variance === null ? <span className="text-gray-400">—</span> : variance === 0 ? (
                        <span className="text-green-600 font-medium">None</span>
                      ) : (
                        <span className={`font-semibold ${variance < 0 ? 'text-red-600' : 'text-green-600'}`}>{variance > 0 ? '+' : ''}{variance}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
                        <StatusIcon size={10} />{cfg.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedTransfer(t)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors" title="View details">
                          <Eye size={13} />
                        </button>
                        {t.status === 'pending_approval' && (
                          <>
                            <button onClick={() => handleApprove(t.id)} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 transition-colors" title="Approve">
                              <CheckCircle size={13} />
                            </button>
                            <button onClick={() => handleReject(t.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors" title="Reject">
                              <XCircle size={13} />
                            </button>
                          </>
                        )}
                        {t.status === 'approved' && (
                          <button onClick={() => handleDispatch(t.id)} className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors" title="Mark in transit">
                            <Truck size={13} />
                          </button>
                        )}
                        {t.status === 'in_transit' && (
                          <button onClick={() => setReceiptTransfer(t)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors" title="Record receipt">
                            <PackageCheck size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-500">Showing {filtered.length} of {transfers.length} transfers</p>
        </div>
      </div>

      {/* Modals */}
      {showNewForm && <NewTransferForm onClose={() => setShowNewForm(false)} onSubmit={handleNewTransfer} />}
      {selectedTransfer && (
        <TransferDetail
          transfer={selectedTransfer}
          onClose={() => setSelectedTransfer(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onDispatch={handleDispatch}
          onReceive={(t) => { setSelectedTransfer(null); setReceiptTransfer(t); }}
        />
      )}
      {receiptTransfer && (
        <ReceiptForm
          transfer={receiptTransfer}
          onClose={() => setReceiptTransfer(null)}
          onSubmit={handleReceipt}
        />
      )}
    </div>
  );
}
